import { useCallback, useState } from 'react';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { performGetRequest } from '@atlassian/jira-fetch';
import { useIntl } from '@atlassian/jira-intl';
import {
	APP_TEAM_NAME,
	X2C_SERVICE_URL_PREFIX,
	useExperienceTracker,
} from '@atlassian/jira-project-create-importer-common';
import { useCloudId, useTenantContext } from '@atlassian/jira-tenant-context-controller';
import { APP_IDENTIFIER } from '../../common/constants';
import { EXPERIENCES, importerSourceInteractionMetric } from '../../common/constants/metrics';
import { useHasJSWLicense } from '../../common/utils';
import type { UseImporterSource } from './types';
import { transformData } from './utils';

const getX2CImporterSourceUrl = (cloudId: string) =>
	`${X2C_SERVICE_URL_PREFIX}/customisedcsv/${cloudId}/products/live`;

export const useImporterSource = (): UseImporterSource => {
	const [onExperienceStart, onExperienceSuccess, onExperienceFail] = useExperienceTracker(
		EXPERIENCES.IMPORTER_SOURCE,
	);
	const cloudId = useCloudId();
	const { formatMessage } = useIntl();
	const [isLoading, setIsLoading] = useState(false);
	const [hasErrorOccurred, setHasErrorOccurred] = useState(false);
	const { isSiteAdmin, isAdmin } = useTenantContext();
	const hasJSWLicense = useHasJSWLicense();

	const fetchImporterSource = useCallback(async () => {
		setIsLoading(true);
		setHasErrorOccurred(false);
		importerSourceInteractionMetric.start();
		onExperienceStart();

		try {
			const data = await performGetRequest(getX2CImporterSourceUrl(cloudId));
			importerSourceInteractionMetric.stop();
			onExperienceSuccess();
			setIsLoading(false);
			return transformData(data, formatMessage, isSiteAdmin, isAdmin, hasJSWLicense);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			importerSourceInteractionMetric.stop();
			onExperienceFail(APP_IDENTIFIER, error);
			fireErrorAnalytics({
				meta: {
					id: 'fetch-importer-source',
					packageName: APP_IDENTIFIER,
					teamName: APP_TEAM_NAME,
				},
				sendToPrivacyUnsafeSplunk: true,
				error,
			});

			setHasErrorOccurred(true);
			setIsLoading(false);
			return null;
		}
	}, [
		cloudId,
		formatMessage,
		hasJSWLicense,
		isSiteAdmin,
		isAdmin,
		onExperienceFail,
		onExperienceStart,
		onExperienceSuccess,
	]);

	return { isLoading, hasErrorOccurred, fetchImporterSource };
};
